/*
  Albatross x Hemisphere — <hm-snap-slider>
  Lightweight carousel built on native CSS scroll-snap (swipe works natively).
  Used by sections hm-image-slider and hm-testimonials.

  Markup contract:
  <hm-snap-slider data-type="slide|loop|fade" data-drag="enabled|disabled|free"
      data-autoplay="true|false" data-speed="5" data-per-move-d="1" data-per-move-m="1"
      data-cols-d="3" data-cols-m="1" data-adaptive-d="false" data-adaptive-m="true"
      data-center="false" data-destroy-d="false" data-destroy-m="false">
    <div class="hm-snap__stage"><ul class="hm-snap__track"><li class="hm-snap__slide">…</li></ul></div>
    <button class="hm-snap__arrow hm-snap__arrow--prev">…</button>
    <div class="hm-snap__dots"></div>
    <button class="hm-snap__arrow hm-snap__arrow--next">…</button>
  </hm-snap-slider>
*/
(() => {
  if (customElements.get('hm-snap-slider')) return;

  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HmSnapSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('.hm-snap__track');
      if (!this.track) return;
      this._ready = true;
      if (this._bound) { this._attach(); this._build(); return; } // re-connected: only re-attach global listeners
      this._bound = true;
      this.slides = Array.from(this.track.children).filter((el) => el.classList.contains('hm-snap__slide'));
      this.prevBtn = this.querySelector('.hm-snap__arrow--prev');
      this.nextBtn = this.querySelector('.hm-snap__arrow--next');
      this.dotsWrap = this.querySelector('.hm-snap__dots');
      this.type = this.dataset.type || 'slide';
      this.isFade = this.type === 'fade';
      this.isLoop = this.type === 'loop';
      this.index = 0;
      this.positions = [0];
      this._paused = false;
      this._hovered = false;
      this._visible = true;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onMq = this._onResize;

      this.prevBtn && this.prevBtn.addEventListener('click', () => { this.go(-1); this._restartAutoplay(); });
      this.nextBtn && this.nextBtn.addEventListener('click', () => { this.go(1); this._restartAutoplay(); });
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._attach();

      // images inside slides may change adaptive height when they load
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this._updateHeight(), { once: true });
      });

      this._initDrag();
      this._initAutoplay();
      this._initEditorEvents();
      this._build();
    }

    /* listeners on window/document/media queries/observers: added on connect, removed on disconnect */
    _attach() {
      MOBILE_MQ.addEventListener ? MOBILE_MQ.addEventListener('change', this._onMq) : MOBILE_MQ.addListener(this._onMq);
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(this.track);
        this.slides.forEach((s) => this._ro.observe(s));
      } else {
        window.addEventListener('resize', this._onResize);
      }
      if (this.autoplay) this._startAutoplayGlobals();
    }

    disconnectedCallback() {
      this._ready = false;
      clearInterval(this._timer);
      this._ro && this._ro.disconnect();
      this._io && this._io.disconnect();
      this._ro = null;
      this._io = null;
      this._unbindDrag && this._unbindDrag();
      this.classList.remove('is-dragging');
      window.removeEventListener('resize', this._onResize);
      MOBILE_MQ.removeEventListener ? MOBILE_MQ.removeEventListener('change', this._onMq) : MOBILE_MQ.removeListener(this._onMq);
      document.removeEventListener('visibilitychange', this._onVis);
    }

    /* ---------- configuration per breakpoint ---------- */
    get isMobile() { return MOBILE_MQ.matches; }
    get perPage() {
      if (this.isFade) return 1;
      return Math.max(1, parseInt(this.isMobile ? this.dataset.colsM : this.dataset.colsD, 10) || 1);
    }
    get perMove() {
      return Math.max(1, parseInt(this.isMobile ? this.dataset.perMoveM : this.dataset.perMoveD, 10) || 1);
    }
    get adaptive() {
      return (this.isMobile ? this.dataset.adaptiveM : this.dataset.adaptiveD) === 'true';
    }
    get destroyed() {
      return (this.isMobile ? this.dataset.destroyM : this.dataset.destroyD) === 'true';
    }
    get center() { return this.dataset.center === 'true'; }

    /* ---------- layout ---------- */
    _build() {
      const n = this.slides.length;
      const per = this.perPage;
      const overflow = !this.destroyed && n > per;
      this.classList.toggle('is-overflow', overflow);
      this.classList.toggle('is-active', !this.destroyed);

      // positions (slide indexes the slider can rest on)
      const pos = [];
      if (this.isFade || this.center) {
        for (let i = 0; i < n; i += this.perMove) pos.push(i);
      } else {
        const last = Math.max(0, n - per);
        for (let i = 0; i < last; i += this.perMove) pos.push(i);
        pos.push(last);
      }
      this.positions = pos.length ? pos : [0];

      this._renderDots();
      this._sync();
      this._updateHeight();
    }

    _onResize() {
      clearTimeout(this._rt);
      this._rt = setTimeout(() => {
        const sig = `${this.isMobile}`;
        if (sig !== this._sig) {
          this._sig = sig;
          this._build();
          this.goToIndex(0, true);
        } else {
          this._sync();
          this._updateHeight();
        }
      }, 60);
    }

    _renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      if (this.positions.length < 2) return;
      const scheme = this.dotsWrap.dataset.scheme;
      this.positions.forEach((slideIndex, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hm-snap__dot' + (scheme ? ` color-${scheme}` : '');
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => { this.goToIndex(slideIndex); this._restartAutoplay(); });
        this.dotsWrap.appendChild(b);
      });
    }

    /* index of the closest resting position for the current scroll offset */
    _currentFromScroll() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (!this.center && t.scrollLeft >= max - 2) return this.positions[this.positions.length - 1];
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((s, i) => {
        const d = Math.abs(this._offsetFor(i) - t.scrollLeft);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }

    _offsetFor(i) {
      const s = this.slides[i];
      if (!s) return 0;
      if (this.center) return s.offsetLeft - (this.track.clientWidth - s.offsetWidth) / 2;
      const padL = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      return s.offsetLeft - padL;
    }

    _onScroll() {
      if (this.isFade) return;
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        this.index = this._currentFromScroll();
        this._sync();
      });
      clearTimeout(this._st);
      this._st = setTimeout(() => { this._updateHeight(); this._handleVideos(); }, 120);
    }

    _posIndex() {
      // position whose slide index is the greatest <= current index
      let p = 0;
      this.positions.forEach((slideIdx, i) => { if (slideIdx <= this.index) p = i; });
      return p;
    }

    _sync() {
      const p = this._posIndex();
      if (this.dotsWrap) {
        Array.from(this.dotsWrap.children).forEach((d, i) => {
          const on = i === p;
          d.classList.toggle('is-active', on);
          if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
      }
      if (!this.isLoop) {
        if (this.prevBtn) this.prevBtn.disabled = p === 0;
        if (this.nextBtn) this.nextBtn.disabled = p === this.positions.length - 1;
      }
      this.slides.forEach((s, i) => {
        const active = this.isFade ? i === this.index : i >= this.index && i < this.index + this.perPage;
        s.classList.toggle('is-active', i === this.index);
        s.classList.toggle('is-visible', active);
        if (this.isFade) s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    /* ---------- navigation ---------- */
    go(dir) {
      const p = this._posIndex();
      let next = p + dir;
      const count = this.positions.length;
      if (next < 0) next = this.isLoop ? count - 1 : 0;
      if (next > count - 1) next = this.isLoop ? 0 : count - 1;
      this.goToIndex(this.positions[next]);
    }

    goToIndex(i, instant) {
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.index = i;
      if (this.isFade) {
        this._sync();
        this._updateHeight();
        this._handleVideos();
        return;
      }
      const behavior = instant || REDUCED_MOTION.matches ? 'auto' : 'smooth';
      this.track.scrollTo({ left: this._offsetFor(i), behavior });
      this._sync();
    }

    _updateHeight() {
      if (!this.adaptive || this.destroyed) { this.track.style.height = ''; return; }
      const s = this.slides[this.index];
      if (!s) return;
      const inner = s.firstElementChild || s;
      this.track.style.height = inner.offsetHeight + 'px';
    }

    /* pause user-started videos in slides that are fully out of view */
    _handleVideos() {
      const tr = this.track.getBoundingClientRect();
      this.slides.forEach((s, i) => {
        let hidden;
        if (this.isFade) hidden = i !== this.index;
        else {
          const r = s.getBoundingClientRect();
          hidden = r.right <= tr.left + 1 || r.left >= tr.right - 1;
        }
        if (!hidden) return;
        s.querySelectorAll('hm-video').forEach((v) => {
          const video = v.querySelector('video');
          if (!video) return;
          const mode = v.dataset.inactive || 'pause';
          if (mode === 'none') return;
          if (mode === 'mute') { video.muted = true; v.classList.add('is-muted'); return; }
          if (v.dataset.autoplay === 'true' && v.classList.contains('is-muted')) return; // IO in hm-base handles it
          if (typeof v.pause === 'function') v.pause(); else video.pause();
        });
      });
    }

    /* ---------- mouse drag (touch swipe is native scroll) ---------- */
    _initDrag() {
      const drag = this.dataset.drag || 'enabled';
      if (drag === 'disabled' || this.isFade) {
        if (this.isFade && drag !== 'disabled') this._initFadeSwipe();
        return;
      }
      const t = this.track;
      let startX = 0; let startScroll = 0; let moved = 0; let down = false; let startIndex = 0;
      t.addEventListener('dragstart', (e) => e.preventDefault());
      const onMove = (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) this.classList.add('is-dragging');
        moved = dx;
        t.scrollLeft = startScroll - dx;
      };
      // window pointer listeners only exist while a drag is in progress
      const unbind = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
      };
      const end = () => {
        unbind();
        if (!down) return;
        down = false;
        this.classList.remove('is-dragging');
        if (!moved) return;
        if (drag === 'free') return;
        const threshold = 40;
        if (moved < -threshold) { this.index = startIndex; this.go(1); }
        else if (moved > threshold) { this.index = startIndex; this.go(-1); }
        else this.goToIndex(startIndex);
        this._restartAutoplay();
      };
      this._unbindDrag = unbind;
      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.destroyed) return;
        unbind(); // never stack listeners if a previous drag did not finish
        down = true; moved = 0; startX = e.clientX; startScroll = t.scrollLeft; startIndex = this.index;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
      });
      // swallow the click that ends a drag so links inside slides don't fire
      t.addEventListener('click', (e) => {
        if (Math.abs(moved) > 4) { e.preventDefault(); e.stopPropagation(); moved = 0; }
      }, true);
    }

    _initFadeSwipe() {
      let x0 = null;
      this.track.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
      this.track.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) { this.go(dx < 0 ? 1 : -1); this._restartAutoplay(); }
      });
    }

    /* ---------- autoplay ---------- */
    _initAutoplay() {
      this.autoplay = this.dataset.autoplay === 'true';
      if (!this.autoplay) return;
      this.speed = Math.max(1, parseFloat(this.dataset.speed) || 5) * 1000;
      this.addEventListener('mouseenter', () => { this._hovered = true; });
      this.addEventListener('mouseleave', () => { this._hovered = false; });
      this.addEventListener('focusin', () => { this._hovered = true; });
      this.addEventListener('focusout', () => { this._hovered = false; });
      this._startAutoplayGlobals();
    }

    /* observer + document listener + timer: re-created on every (re)connect, torn down in disconnectedCallback */
    _startAutoplayGlobals() {
      if (this._io) this._io.disconnect();
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((en) => { this._visible = en[0].isIntersecting; }, { threshold: 0.2 });
        this._io.observe(this);
      }
      if (!this._onVis) this._onVis = () => { if (document.hidden) this._hovered = false; };
      document.removeEventListener('visibilitychange', this._onVis);
      document.addEventListener('visibilitychange', this._onVis);
      this._restartAutoplay();
    }

    _restartAutoplay() {
      if (!this.autoplay) return;
      clearInterval(this._timer);
      this._timer = setInterval(() => {
        if (this._paused || this._hovered || !this._visible || document.hidden || this.destroyed) return;
        if (this.positions.length < 2) return;
        const p = this._posIndex();
        if (p >= this.positions.length - 1) this.goToIndex(this.positions[0]);
        else this.go(1);
      }, this.speed);
    }

    /* ---------- theme editor ---------- */
    _initEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target));
        this._paused = true;
        if (i > -1 && !this.destroyed) this.goToIndex(Math.min(i, this.positions[this.positions.length - 1] ?? i), true);
      });
      this.addEventListener('shopify:block:deselect', () => { this._paused = false; });
    }
  }

  customElements.define('hm-snap-slider', HmSnapSlider);
})();
